import Link from 'next/link';
import { FC } from 'react';
import { IoChevronForward, IoPeopleOutline } from 'react-icons/io5';
import { TbPointFilled } from 'react-icons/tb';

import { Badge, Box, BoxProps, Flex, Group, Stack, Text } from '@mantine/core';
import { useElementSize, useIntersection } from '@mantine/hooks';
import useSWRImmutable from 'swr/immutable';

import { PermissionProject, Project, ProjectTeaser as ProjectTeaserType, PublishedStatus } from 'models';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { SettingsDropdown } from 'components/dropdown/SettingsDropdown';
import { IKImage } from 'components/ik-image/IKImage';
import { ProjectMetaItem } from 'components/project-overview/components/ProjectList';
import { useProjectMeta } from 'components/project-overview/hooks';
import { ProjectTeaserPreview } from 'components/project-teaser/ProjectTeaserPreview';
import { ProjectTeaserUpdateDot } from 'components/project-teaser/ProjectTeaserUpdateDot';
import { PublishedBadge } from 'components/published-badge/PublishedBadge';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { useCurrentUser } from 'hooks/use-current-user';
import { useIsMobile } from 'hooks/use-is-mobile';
import { useTeam } from 'hooks/use-team';
import { ProjectService } from 'services/ProjectService';

import { DeleteButton, DuplicateButton, MoveToTeamButton, ProjectTeaser } from './ProjectTeaser';
import { ProjectTeaserCollaborators } from './ProjectTeaserCollaborators';
import { ProjectTeaserFollow } from './ProjectTeaserFollow';
import { ProjectTeaserLatestMessage } from './ProjectTeaserLatestMessage';
import { ProjectTeaserUpdated } from './ProjectTeaserUpdated';

import cx from './ProjectTeaserHorizontal.module.scss';

type ProjectTeaserHorizontalProps = {
    project: Project | ProjectTeaserType;
    highlightTeam?: boolean;
    showLatestMessage?: boolean;
    hideMeta?: boolean;
} & BoxProps;

const ProjectTeaserHorizontal: FC<ProjectTeaserHorizontalProps> = ({
    project,
    highlightTeam,
    showLatestMessage,
    hideMeta,
    ...boxProps
}) => {
    const user = useCurrentUser();
    const teams = useCurrentTeams();
    const { team } = useTeam(project.team || undefined);
    const { projectUrl, teamName } = useProjectMeta(project);

    const canMoveTeam = TeamHelpers.canMoveProject(team, user, teams);
    const canDelete = TeamHelpers.hasUserPermission(team, user, PermissionProject.DELETE);

    const { ref: intersectionRef, entry } = useIntersection({ threshold: 0.1 });
    const teaserIsVisible = !!entry?.isIntersecting;

    const { data: teaserData, isLoading } = useSWRImmutable(
        user && teaserIsVisible ? `/api/projects/${project.id}/teaser` : null,
        () => ProjectService.getTeaser(project.id),
        { keepPreviousData: true },
    );

    const { ref: sizeRef, width: containerWidth } = useElementSize();

    return (
        <Box ref={intersectionRef} className={cx.wrapper} component={Link} href={projectUrl} {...boxProps}>
            <Box className={cx.inner}>
                <Flex ref={sizeRef} className={cx.preview} align="center" justify="center">
                    {project.thumbnail ? (
                        <IKImage
                            fileOrId={project.thumbnail}
                            width={containerWidth || 400}
                            height={containerWidth ? Math.round((containerWidth * 9) / 16) : 225}
                            alt={project.name}
                            w="100%"
                            h="100%"
                            fit="cover"
                        />
                    ) : (
                        <ProjectTeaserPreview
                            projectId={project.id}
                            isVisible={teaserIsVisible}
                            containerWidth={containerWidth}
                        />
                    )}
                </Flex>

                <Box
                    className={[cx.details, showLatestMessage && cx.withLatestMessage, hideMeta && cx.withoutMeta]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <Stack justify="center" gap={4}>
                        {teamName &&
                            (highlightTeam ? (
                                <Badge
                                    radius={0}
                                    px={8}
                                    mb="xs"
                                    tt="none"
                                    fw={500}
                                    leftSection={<IoPeopleOutline size={12} />}
                                    style={{ alignSelf: 'flex-start' }}
                                >
                                    {teamName}
                                </Badge>
                            ) : (
                                <ProjectMetaItem
                                    label={teamName}
                                    c="gray.6"
                                    afterIcon={<IoChevronForward size={12} color="var(--mantine-color-gray-4)" />}
                                />
                            ))}

                        <Text size="lg" fw={600} lh={1.2}>
                            {project.name} <ProjectTeaserUpdateDot project={project} lastView={teaserData?.lastView} />
                        </Text>

                        <Group gap={4} data-hidden-if-empty mt="xs">
                            {!!teaserData?.nbUnreadMessages && (
                                <Badge size="xs" color="red" variant="light" leftSection={<TbPointFilled size={10} />}>
                                    {teaserData?.nbUnreadMessages} new{' '}
                                    {teaserData?.nbUnreadMessages === 1 ? 'message' : 'messages'}
                                </Badge>
                            )}

                            {project.isReferenceDesign && (
                                <PublishedBadge
                                    status={
                                        project.visibility === 'marketplace'
                                            ? PublishedStatus.PUBLISHED
                                            : PublishedStatus.DRAFT
                                    }
                                    size="xs"
                                    variant="light"
                                    publishedLabel="Published design"
                                    notPublishedLabel="Unpublished design"
                                />
                            )}
                        </Group>
                    </Stack>

                    {!hideMeta && (
                        <>
                            <ProjectTeaserUpdated project={project} />

                            <ProjectMetaItem
                                label={(project.tags ?? []).join(', ')}
                                emptyLabel="[no tags]"
                                isLoading={isLoading}
                            />
                        </>
                    )}

                    {showLatestMessage &&
                        (teaserData?.latestMessage ? (
                            <ProjectTeaserLatestMessage message={teaserData.latestMessage} projectUrl={projectUrl} />
                        ) : (
                            <ProjectMetaItem label={null} emptyLabel="[no messages]" isLoading={isLoading} />
                        ))}

                    <ProjectTeaserCollaborators
                        users={teaserData?.collaboratorUsers}
                        manufacturers={teaserData?.collaboratorManufacturers}
                        isLoading={isLoading}
                    />
                </Box>
            </Box>

            <Group data-project-action gap={4}>
                <Box onClick={(e) => e.preventDefault()}>
                    <SettingsDropdown>
                        <DuplicateButton project={project} />
                        {canMoveTeam && <MoveToTeamButton project={project} />}
                        {canDelete && <DeleteButton project={project} />}
                    </SettingsDropdown>
                </Box>
                <ProjectTeaserFollow projectId={project.id} team={team} isVisible={!!teaserIsVisible} />
            </Group>
        </Box>
    );
};

const WrappedProjectTeaserHorizontal = (props: ProjectTeaserHorizontalProps) => {
    const isMobile = useIsMobile();

    if (isMobile) {
        return <ProjectTeaser {...props} />;
    }

    return <ProjectTeaserHorizontal {...props} />;
};

export { WrappedProjectTeaserHorizontal as ProjectTeaserHorizontal };
